import { AlertCircle, Copy05, Eye, InfoCircle, Mail01, Phone01, Plus, XClose } from '@untitled-ui/icons-react/build/cjs';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ButtonCmp from 'src/components/ButtonCmp';
import CommonModal from 'src/components/CommonModal';
import InputText from 'src/components/InputText/InputText';
import SelectBox from 'src/components/SelectBox/SelectBox';
import TooltipCmp from 'src/components/TooltipCmp';
import { ORDER_STATUS, REGULAR_EXPRESSION_NEW, SHARE_TRACKING_LINK_STATUSES } from 'src/constants/common';
import { ROUTES, SHARE_LINK_PREVIEW_PARAM } from 'src/constants/routes';
import { ITrackingNotificationRecipient } from 'src/services/CustomerService';
import { logError } from 'src/services/LoggingService';
import {
    getOrderShareLink,
    getOrderTrackingRecipients,
    IOrderShareLinkResponse,
    IOrderTrackingNotificationRecipient,
    ISaveOrderTrackingRecipientsResponse,
    saveOrderTrackingRecipients,
} from 'src/services/OrderService';
import { copyToClipboard, usePhone } from 'src/utils/CommonFunctions';
import { buildPublicTrackingUrl, isPublicTrackingStatus } from 'src/utils/publicTrackingLink';
import WalToast from 'src/utils/WalToast';

interface IProps {
    handleClose: (status: boolean) => void;
    /** Route-level order id (orders.id), same value OrderHeaderActions receives. */
    orderDbId: string;
    orderStatus?: string | null;
    /** The customer-facing id (orders."orderId", e.g. "WAL-TC3B-2925") — what the shared URL carries. */
    walId?: string | null;
}

/**
 * One row of the working list. The modal stages edits locally and commits them with a single
 * "Save & send link" press (client decision, 2026-09-30 call) — `isNew` is what drives the purple
 * notice and the row badge, and on the server it is what decides who gets the first-contact
 * notification. `value` is the canonical form (email lowercased, phone E.164) and doubles as the
 * row key, so two spellings of the same number can never both sit in the list.
 */
interface IStagedRecipient {
    type: 'email' | 'phone';
    value: string;
    isNew: boolean;
}

interface IRecipientOption {
    /** Canonical value — the dropdown deals in values, not pool ids, because a typed-but-unsaved
     *  entry has no id until the save creates it. */
    value: string;
    label: string;
    type: 'email' | 'phone';
    /** Digits of a phone value, so "8200902620" or "(416) 306-1172" still finds the formatted row. Empty for emails. */
    digits: string;
    /** Set on entries already on the working list — react-select refuses to select them. */
    isDisabled?: boolean;
    /** SelectBox shows the row checkbox ticked for this flag even though the option is not in `value`. */
    isChecked?: boolean;
}

/**
 * Same rule as the WAL Order Detail "Share" button (wal-frontend-v2 origin/dev, OrderDetailV2 customerShareLink):
 *   booked … delivered, and the billing statuses after it → public tracking link (…/?walId=WAL-XXXX-123)
 *   anything else                                         → no link to share yet
 *
 * The readable WAL ID URL is what a customer can repeat over the phone, so it is preferred whenever
 * one can be built. The token branches below are the fallback for a row that reached the modal
 * without its WAL ID; they also keep the tokens meaningful, which is why they are not deleted. Both
 * tokens still come from the CP backend so a fallback link is identical to the one WAL staff copy.
 */
const buildShareUrl = (orderStatus: string | null | undefined, tokens: IOrderShareLinkResponse | null, walId: string | null | undefined): string | null => {
    if (!tokens) return null;

    if (isPublicTrackingStatus(orderStatus)) {
        const publicTrackingUrl = buildPublicTrackingUrl(walId);
        if (publicTrackingUrl) return publicTrackingUrl;
    }

    const origin = window.location.origin;

    if (orderStatus === ORDER_STATUS.BOOKED && tokens.orderToken) {
        return `${origin}${ROUTES.SHARE_ORDER_LINK}?t=${tokens.orderToken}`;
    }

    if (SHARE_TRACKING_LINK_STATUSES.includes(orderStatus as string) && tokens.radarToken) {
        return `${origin}${ROUTES.SHARE_RADAR_ORDER_LINK}?t=${tokens.radarToken}`;
    }

    return null;
};

const PHONE_SHAPE_REGEX = /^\+?[\d\s().-]+$/;

/**
 * SelectBox applies this to the portaled menu wrapper. react-select tags unselectable options with
 * `select__option--is-disabled`; matched via [class*=…] because Tailwind reads `_` in arbitrary
 * variants as a space, so recipients already on the list render greyed out instead of
 * looking clickable — without touching the shared component or global CSS.
 */
const DISABLED_OPTION_CLASS = '[&_[class*=option--is-disabled]]:opacity-50 [&_[class*=option--is-disabled]]:cursor-not-allowed';

const digitsOnly = (raw: string): string => raw.replace(/\D/g, '');

/**
 * Client-side normalization mirrors the backend (utils/trackingRecipients.ts): email → lowercased,
 * phone → E.164 via libphonenumber-js (US/CA default region). Returns null when it is neither.
 */
const normalizeRecipient = (raw: string): IStagedRecipient | null => {
    const trimmed = raw.trim();
    if (!trimmed) return null;

    if (REGULAR_EXPRESSION_NEW.EMAIL.test(trimmed)) return { type: 'email', value: trimmed.toLowerCase(), isNew: true };

    // Only attempt phone parsing on phone-looking input so "abc@" doesn't get treated as digits
    if (PHONE_SHAPE_REGEX.test(trimmed)) {
        const parsed = parsePhoneNumberFromString(trimmed, 'US');
        if (parsed?.isValid()) return { type: 'phone', value: parsed.number, isNew: true };
    }

    return null;
};

const ShareTrackingLinkModal = ({ handleClose, orderDbId, orderStatus, walId }: IProps) => {
    const { t } = useTranslation();
    const { getFormatPhoneWithPlus } = usePhone();
    /**
     * usePhone() rebuilds its helpers on every render, so depending on getFormatPhoneWithPlus
     * directly made formatRecipient — and therefore recipientOptions and filteredOptions — a new
     * value on EVERY render, not just when something actually changed. react-select compares
     * options by reference, so it was being handed a brand-new list constantly. The helper is pure,
     * so holding it in a ref is safe and lets the memos below do their job.
     */
    const formatPhoneRef = useRef(getFormatPhoneWithPlus);
    formatPhoneRef.current = getFormatPhoneWithPlus;

    const [shareTokens, setShareTokens] = useState<IOrderShareLinkResponse | null>(null);
    const [isLinkLoading, setIsLinkLoading] = useState(true);

    /** Customer-wide pool (customer_master) — only used to suggest people added on earlier orders. */
    const [pool, setPool] = useState<ITrackingNotificationRecipient[]>([]);
    /** What the server currently has on THIS order — the baseline every staged edit is diffed against. */
    const [savedRecipients, setSavedRecipients] = useState<IOrderTrackingNotificationRecipient[]>([]);
    /** The working list the user sees. Nothing here is persisted until "Save & send link". */
    const [staged, setStaged] = useState<IStagedRecipient[]>([]);
    const [isRecipientsLoading, setIsRecipientsLoading] = useState(true);

    const [searchInput, setSearchInput] = useState('');
    const [recipientError, setRecipientError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    /**
     * Pick the most helpful error for what the user seems to be typing: an "@" means an email
     * attempt, a digit-heavy value means a phone attempt, anything else gets the generic hint.
     */
    const getFormatErrorMessage = (raw: string) => {
        const trimmed = raw.trim();
        if (trimmed.includes('@')) return t('Orders.Share_Tracking_Link.Invalid_Email');
        if (PHONE_SHAPE_REGEX.test(trimmed)) return t('Orders.Share_Tracking_Link.Invalid_Phone');

        return t('Orders.Share_Tracking_Link.Incorrect_Formatting');
    };

    const toStaged = (recipients: IOrderTrackingNotificationRecipient[]): IStagedRecipient[] => recipients.map((recipient) => ({ type: recipient.type, value: recipient.value, isNew: false }));

    useEffect(() => {
        let isMounted = true;

        getOrderShareLink(orderDbId)
            .then((response: any) => {
                if (!isMounted) return;
                setShareTokens(response?.data?.data ?? response?.data ?? null);
            })
            .catch(async (err: any) => {
                await logError(err, {
                    errorMessage: err?.message || 'Error fetching order share link',
                    errorLocation: 'ShareTrackingLinkModal: getOrderShareLink',
                    storeInDatabase: true,
                    logToConsole: true,
                });
            })
            .finally(() => isMounted && setIsLinkLoading(false));

        getOrderTrackingRecipients(orderDbId)
            .then((response: any) => {
                if (!isMounted) return;
                const data = response?.data?.data ?? response?.data ?? {};
                const recipients: IOrderTrackingNotificationRecipient[] = Array.isArray(data.recipients) ? data.recipients : [];
                setSavedRecipients(recipients);
                setStaged(toStaged(recipients));
                setPool(Array.isArray(data.availableRecipients) ? data.availableRecipients : []);
            })
            .catch(async (err: any) => {
                await logError(err, {
                    errorMessage: err?.message || 'Error fetching order tracking recipients',
                    errorLocation: 'ShareTrackingLinkModal: getOrderTrackingRecipients',
                    storeInDatabase: true,
                    logToConsole: true,
                });
                // Surface the failure — otherwise the list just looks empty and hides a 500
                WalToast.error(err?.response?.data?.message || t('Orders.Share_Tracking_Link.Load_Failed'));
            })
            .finally(() => isMounted && setIsRecipientsLoading(false));

        return () => {
            isMounted = false;
        };
        // orderDbId is route-level and never changes while this modal is mounted
    }, [orderDbId]);

    const shareUrl = useMemo(() => buildShareUrl(orderStatus, shareTokens, walId), [orderStatus, shareTokens, walId]);

    const formatRecipient = useCallback((recipient: Pick<IStagedRecipient, 'type' | 'value'>) => (recipient.type === 'phone' ? formatPhoneRef.current(recipient.value) : recipient.value), []);

    /**
     * Display rule for the "Add …" row: once what was typed parses as a real number, show it the way
     * the staged rows show it. Emails and still-incomplete numbers stay exactly as typed so the label
     * does not reflow on every keystroke. Purely cosmetic — handleTypedRecipient still gets the raw
     * text and normalizes it itself.
     */
    const formatCreateLabelValue = useCallback(
        (typed: string) => {
            const normalized = normalizeRecipient(typed);

            return normalized?.type === 'phone' ? formatRecipient(normalized) : typed;
        },
        [formatRecipient]
    );

    const stagedValues = useMemo(() => new Set(staged.map((recipient) => recipient.value.toLowerCase())), [staged]);

    const newCount = useMemo(() => staged.filter((recipient) => recipient.isNew).length, [staged]);
    const removedCount = useMemo(() => savedRecipients.filter((recipient) => !stagedValues.has(recipient.value.toLowerCase())).length, [savedRecipients, stagedValues]);
    const hasChanges = newCount > 0 || removedCount > 0;

    /**
     * Dropdown options: the whole customer pool, so someone added on an earlier order is one click
     * instead of one retype. Entries already on the working list stay listed but are disabled and
     * ticked (greyed via DISABLED_OPTION_CLASS) rather than hidden, so the user can see they are
     * already covered instead of wondering where they went.
     */
    const recipientOptions: IRecipientOption[] = useMemo(
        () =>
            pool.map((recipient) => {
                const isStaged = stagedValues.has(recipient.value.toLowerCase());

                return {
                    value: recipient.value,
                    label: formatRecipient(recipient),
                    type: recipient.type,
                    digits: recipient.type === 'phone' ? digitsOnly(recipient.value) : '',
                    isDisabled: isStaged,
                    isChecked: isStaged,
                };
            }),
        [pool, stagedValues, formatRecipient]
    );

    // Passing onInputChange turns off react-select's built-in filter, so filter ourselves — and more
    // forgivingly than a label substring: the labels are DISPLAY-formatted ("+91 82009 02620"), so a
    // user typing the number the way they know it ("8200902620", "+918200902620", "(416) 306-1172")
    // must still see the saved row and be able to pick it instead of being offered only "Add …".
    // Emails match on label/value text as before.
    const normalizedSearch = searchInput.trim().toLowerCase();
    const searchDigits = digitsOnly(normalizedSearch);
    const filteredOptions = useMemo(() => {
        if (!normalizedSearch) return recipientOptions;

        return recipientOptions.filter((option) => {
            if (option.label.toLowerCase().includes(normalizedSearch) || option.value.toLowerCase().includes(normalizedSearch)) return true;

            // Digit match only for phone rows and only once there are enough digits to be meaningful —
            // a single "1" would otherwise light up every phone in the pool.
            return option.digits !== '' && searchDigits.length >= 3 && option.digits.includes(searchDigits);
        });
    }, [recipientOptions, normalizedSearch, searchDigits]);

    // The typed text IS a saved recipient — by label, by stored value, or once normalized (so
    // "+918200902620" and "+91 82009 02620" are the same entry). When that entry is still pickable,
    // hide the "Add …" row so Enter picks the saved entry instead of adding a duplicate. When it is
    // already on the list (disabled option), KEEP the row: react-select never focuses a disabled
    // option, so without it Enter would silently do nothing — via the create path the typed value
    // reaches handleTypedRecipient, which shows the "already added" message.
    const exactMatch = useMemo(() => {
        if (!normalizedSearch) return undefined;

        const normalizedTyped = normalizeRecipient(searchInput)?.value.toLowerCase();

        return recipientOptions.find(
            (option) => option.label.toLowerCase() === normalizedSearch || option.value.toLowerCase() === normalizedSearch || (!!normalizedTyped && option.value.toLowerCase() === normalizedTyped)
        );
    }, [recipientOptions, normalizedSearch, searchInput]);
    const showCreateOption = !exactMatch || !!exactMatch.isDisabled;

    const handleCopyLink = () => {
        if (!shareUrl) return;
        copyToClipboard(shareUrl);
        WalToast.copy(t('Common.Copied_to_clipboard'), '', 'copy-msg');
    };

    /**
     * Opens the public tracking page in a new tab so the user sees exactly what a recipient will see.
     *
     * The preview flag is added HERE rather than in buildShareUrl on purpose: `shareUrl` is what
     * the Copy button puts on the clipboard and what gets sent to recipients, so it has to stay
     * clean. Without the flag the share page redirects a signed-in user straight to the
     * authenticated detail page and the preview never renders.
     */
    const handlePreviewLink = () => {
        if (!shareUrl) return;
        const previewUrl = new URL(shareUrl);

        // The flag only means anything to the /share-links/* routes, which redirect a signed-in user
        // to the authenticated detail page. The public tracking page has no such redirect, so adding
        // it there would put a stray parameter in front of the customer for no reason.
        if (!previewUrl.searchParams.has('walId')) {
            previewUrl.searchParams.set(SHARE_LINK_PREVIEW_PARAM, '1');
        }
        window.open(previewUrl.toString(), '_blank', 'noopener,noreferrer');
    };

    /**
     * Stage one recipient. `isNew` is decided against what the SERVER has, not against the working
     * list: removing a saved recipient and adding them back in the same session must not turn them
     * into a "new" row that gets re-notified on save.
     */
    const stageRecipient = (candidate: IStagedRecipient) => {
        if (stagedValues.has(candidate.value.toLowerCase())) {
            setRecipientError(t('Orders.Share_Tracking_Link.Already_Added'));

            return;
        }

        const wasAlreadySaved = savedRecipients.some((recipient) => recipient.value.toLowerCase() === candidate.value.toLowerCase());

        setStaged((current) => [...current, { ...candidate, isNew: !wasAlreadySaved }]);
        setSearchInput('');
        setRecipientError(null);
    };

    /**
     * Enter on a typed value (react-select's create path): validate the format, then stage it.
     * Nothing is written anywhere — the save is what creates the customer-pool entry and attaches
     * it to the order.
     */
    const handleTypedRecipient = (inputValue: string) => {
        const normalized = normalizeRecipient(inputValue);

        if (!normalized) {
            setRecipientError(getFormatErrorMessage(inputValue));

            return;
        }

        stageRecipient(normalized);
    };

    /** Picking a saved recipient in the dropdown stages it. Attached entries are disabled options,
     *  so they can never be picked; the filter is a safety net in case one slips through via keyboard. */
    const handlePickRecipient = (event: any) => {
        const picked: IRecipientOption[] = Array.isArray(event) ? event : [];

        picked.filter((option) => !stagedValues.has(option.value.toLowerCase())).forEach((option) => stageRecipient({ type: option.type, value: option.value, isNew: true }));
    };

    /** X on a row: staged only — nothing leaves the order until Save. */
    const handleRemoveRow = (value: string) => {
        setStaged((current) => current.filter((recipient) => recipient.value !== value));
        setRecipientError(null);
    };

    /** Report what actually happened: saved, saved + sent, saved + partially sent, saved + nothing sent. */
    const reportSaveOutcome = (data: ISaveOrderTrackingRecipientsResponse) => {
        if (data.addedCount === 0) {
            WalToast.success(t('Orders.Share_Tracking_Link.Saved'));

            return;
        }

        if (!data.linkAvailable) {
            // Recipients are stored and will get future updates; this order simply has no
            // shareable page yet (not dispatched), so there was nothing to send them.
            WalToast.success(t('Orders.Share_Tracking_Link.Saved_No_Link_Yet'));

            return;
        }

        const { sent, failed } = data.notified;

        if (failed === 0) {
            WalToast.success(t('Orders.Share_Tracking_Link.Saved_And_Sent', { count: sent }));
        } else if (sent === 0) {
            WalToast.error(t('Orders.Share_Tracking_Link.Saved_None_Sent'));
        } else {
            WalToast.error(t('Orders.Share_Tracking_Link.Saved_Partially_Sent', { sent, total: sent + failed }));
        }
    };

    const handleSave = async () => {
        if (isSaving) return;

        setIsSaving(true);
        setRecipientError(null);

        try {
            const response = await saveOrderTrackingRecipients(
                orderDbId,
                staged.map((recipient) => recipient.value)
            );
            const data: ISaveOrderTrackingRecipientsResponse = response?.data?.data ?? response?.data ?? {};

            const recipients = Array.isArray(data.recipients) ? data.recipients : [];
            setSavedRecipients(recipients);
            setStaged(toStaged(recipients));
            if (Array.isArray(data.pool)) setPool(data.pool);

            reportSaveOutcome(data);
        } catch (err: any) {
            if (err?.response?.status === 400) {
                // Server rejected a value our own normalizer let through — show it on the field
                setRecipientError(err?.response?.data?.errorMessages?.[0]?.message || err?.response?.data?.message || t('Orders.Share_Tracking_Link.Save_Failed'));
            } else {
                await logError(err, {
                    errorMessage: err?.message || 'Error saving order tracking recipients',
                    errorLocation: 'ShareTrackingLinkModal: handleSave',
                    storeInDatabase: true,
                    logToConsole: true,
                });
                WalToast.error(err?.response?.data?.message || t('Orders.Share_Tracking_Link.Save_Failed'));
            }
        } finally {
            setIsSaving(false);
        }
    };

    const renderRecipientRow = (recipient: IStagedRecipient) => {
        const Icon = recipient.type === 'phone' ? Phone01 : Mail01;

        return (
            <li key={recipient.value} className="px-3 py-2 flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg border border-utilityGray200 bg-gray50 flex items-center justify-center flex-none">
                    <Icon className="w-4 h-4 text-grayLight600" />
                </span>
                <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium leading-[1.5] text-grayLight900 truncate">{formatRecipient(recipient)}</p>
                    <p className="text-xs font-normal leading-[1.5] text-grayLight600">
                        {recipient.type === 'phone' ? t('Orders.Share_Tracking_Link.Channel_Sms') : t('Orders.Share_Tracking_Link.Channel_Email')}
                    </p>
                </div>
                <TooltipCmp message={t('Orders.Share_Tracking_Link.Remove_Recipient')} position="top" childrenClassName="flex">
                    <ButtonCmp
                        type="button"
                        size="none"
                        className="text-grayLight600 rounded-md px-0.5 flex-none"
                        onClick={() => handleRemoveRow(recipient.value)}
                        disabled={isSaving}
                        icon={<XClose className="w-4 h-4" />}
                    >
                        <></>
                    </ButtonCmp>
                </TooltipCmp>
            </li>
        );
    };

    return (
        <CommonModal
            title={t('Orders.Share_Tracking_Link.Title')}
            handleClose={() => handleClose(false)}
            size="max-w-[688px]"
            isOverflow={true}
            closeOnOutsideClick={true}
            secondaryBtnText={t('Common.Close')}
            secondaryBtnOnClick={() => handleClose(false)}
            // "Save & send link" only when the save will actually send something. A save that only
            // drops recipients notifies nobody, so promising a link would be a lie.
            primaryBtnText={newCount > 0 ? t('Orders.Share_Tracking_Link.Save_And_Send') : t('Common.Save')}
            primaryBtnOnClick={handleSave}
            primaryBtnLoading={isSaving}
            primaryBtnDisabled={isRecipientsLoading || isSaving || !hasChanges}
        >
            <div className="p-4 flex flex-col gap-4">
                {/* Section 1 — tracking link */}
                <section className="rounded-[10px] border border-utilityGray200 bg-gray25 p-3 flex flex-col gap-3">
                    <div className="flex flex-col">
                        <h6 className="text-xs font-semibold leading-[1.5] text-textSecondary">{t('Orders.Share_Tracking_Link.Link_Section_Title')}</h6>
                        <p className="text-xs font-normal leading-[1.5] text-grayLight600">{t('Orders.Share_Tracking_Link.Link_Section_Desc')}</p>
                    </div>
                    <div className="flex items-end gap-2.5">
                        <InputText
                            inputName="shareTrackingLink"
                            value={isLinkLoading ? '' : shareUrl ?? t('Orders.Share_Tracking_Link.Link_Not_Available')}
                            readOnly
                            disabled
                            parentClassName={`flex-1 ${isLinkLoading ? 'custom-loading' : ''}`}
                            icon={
                                shareUrl ? (
                                    <span className="absolute right-px top-px bottom-px flex items-center pl-2 pr-3 bg-gray50 rounded-r-lg">
                                        <TooltipCmp message={t('Common.Copy')} position="top" childrenClassName="flex">
                                            <Copy05 className="cursor-pointer w-3.5 h-3.5 text-grayLight900" onClick={handleCopyLink} />
                                        </TooltipCmp>
                                    </span>
                                ) : undefined
                            }
                        />
                        <ButtonCmp
                            type="button"
                            size="md"
                            className="btn_secondary_black w-[100px]"
                            icon={<Eye className="w-4 h-4" />}
                            disabled={isLinkLoading || !shareUrl}
                            onClick={handlePreviewLink}
                        >
                            {t('Common.Preview')}
                        </ButtonCmp>
                    </div>
                    <p className="text-xs font-normal leading-[1.5] text-grayLight600">{t('Orders.Share_Tracking_Link.Link_One_Per_Order')}</p>
                </section>

                {/* Section 2 — notification recipients for this order */}
                <section className="rounded-[10px] border border-utilityGray200 bg-gray25 p-3 flex flex-col gap-3">
                    <div className="flex flex-col">
                        <h6 className="text-xs font-semibold leading-[1.5] text-textSecondary">{t('Orders.Share_Tracking_Link.Notifications_Title')}</h6>
                        <p className="text-xs font-normal leading-[1.5] text-grayLight600">{t('Orders.Share_Tracking_Link.Notifications_Desc')}</p>
                    </div>

                    {/* Picking a saved recipient, or pressing Enter on a typed value, stages it — nothing is
                        written until "Save & send link". Errors surface as a red border plus an alert icon
                        whose tooltip carries the specific message (same treatment as WAL's modal). */}
                    <div className="relative">
                        <SelectBox
                            label={t('Orders.Share_Tracking_Link.Add_Recipient_Label')}
                            labelClassName="form_label block mb-1.5"
                            options={filteredOptions}
                            // Never holds a selection — every pick becomes a row below (see handlePickRecipient)
                            value={[]}
                            onInputChange={(value: string) => {
                                setSearchInput(value ?? '');
                                if (recipientError && value) setRecipientError(null);
                            }}
                            onChangeFunc={handlePickRecipient}
                            placeholder={t('Orders.Share_Tracking_Link.Recipient_Placeholder')}
                            isMultipleSelection={true}
                            isMultipleEquipmentType={true}
                            isSearchable={true}
                            // Only a click or Enter adds a recipient. Without this, Tab (and Space on an empty search box)
                            // makes react-select commit whichever row happens to be focused, so merely tabbing through the
                            // open menu ticked a checkbox and attached someone the user never picked.
                            isPreventKeyboardSelect={true}
                            forceSearchable={true}
                            // No clear-all indicator: nothing is ever selected, and it keeps the error icon's slot free
                            isClearable={false}
                            closeMenuOnSelect={false}
                            keepMenuOpenOnMultiSelect={true}
                            // Creatable: Enter on "<typed value>" validates the format and stages it
                            autoCompleteSelect={true}
                            onCreateOption={handleTypedRecipient}
                            isValidNewOption={showCreateOption}
                            // "Add …" FIRST so Enter always acts on what was typed. With it last, react-select
                            // focuses the first partial match, and Enter would stage that suggestion instead of
                            // the typed value; suggestions remain one arrow-down away.
                            createOptionPosition="first"
                            formatCreateLabelIcon={<Plus className="w-4 h-4 text-primary flex-none" />}
                            // Blue label, matching the "Add New …" create rows elsewhere (e.g. Add New Shipper)
                            formatCreateLabelTextClass="text-primary"
                            formatCreateLabelValue={formatCreateLabelValue}
                            formatCreateLabelText={t('Orders.Share_Tracking_Link.Create_Recipient')}
                            noOptionMessage={t('Orders.Share_Tracking_Link.No_Saved_Recipients')}
                            SelectDropdownWrapClass={DISABLED_OPTION_CLASS}
                            // Each pick calls setStaged, and SelectBox re-renders several more times on
                            // its own (setInputValue, setIsFocused). Without this, react-select gets a new
                            // `Option` component type on each of those renders and remounts every row of
                            // the still-open menu — the list visibly flickers on click.
                            isStableOptionRender={true}
                            isDisabled={isRecipientsLoading || isSaving}
                            isLoading={isRecipientsLoading}
                            // Red border only; the message lives in the alert icon's tooltip beside it
                            isError={!!recipientError}
                            isShowErrorMsg={false}
                            classComp="!bg-white"
                        />
                        {recipientError && (
                            // right-9 leaves room for the select's own chevron indicator
                            <span className="absolute right-9 top-[calc(50%+9px)] -translate-y-1/2 z-[1]">
                                <TooltipCmp message={recipientError} position="top" childrenClassName="flex">
                                    <AlertCircle className="h-4 w-4 text-fgErrorPrimary cursor-pointer" />
                                </TooltipCmp>
                            </span>
                        )}
                    </div>

                    {isRecipientsLoading ? (
                        <ul className="rounded-xl border border-utilityGray200 bg-white shadow-xs divide-y divide-utilityGray200">
                            {[1, 2].map((item) => (
                                <li key={item} className="px-3 py-2 flex items-center gap-3">
                                    <span className="custom-loading w-8 h-8 rounded-lg flex-none" aria-hidden="true" />
                                    <span className="custom-loading text-xs font-normal leading-[1.5]" aria-hidden="true">
                                        lorem.ipsum@gmail.com
                                    </span>
                                </li>
                            ))}
                        </ul>
                    ) : staged.length > 0 ? (
                        <ul className="rounded-xl border border-utilityGray200 bg-white shadow-xs divide-y divide-utilityGray200 max-h-[240px] overflow-y-auto custom-scrollbar-v2">
                            {staged.map(renderRecipientRow)}
                        </ul>
                    ) : (
                        <p className="text-xs font-normal italic text-grayLight600">{t('Orders.Share_Tracking_Link.No_Recipients')}</p>
                    )}

                    {/* The promise Lucas asked for in so many words: say up front who is about to be
                        texted/emailed, so nobody presses Save wondering whether anything goes out. */}
                    {newCount > 0 && (
                        <div className="rounded-lg border border-utilityBrand200 bg-primary50 px-3 py-2 flex items-center gap-2">
                            <InfoCircle className="w-4 h-4 text-primary700 flex-none" />
                            <p className="text-xs font-normal leading-[1.5] text-primary700">
                                <span className="font-semibold">{t('Orders.Share_Tracking_Link.Pending_Notice_Count', { count: newCount })}</span>{' '}
                                {t('Orders.Share_Tracking_Link.Pending_Notice_Suffix')}
                            </p>
                        </div>
                    )}
                </section>
            </div>
        </CommonModal>
    );
};

export default ShareTrackingLinkModal;
